import { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Send, Minimize2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [showProactivePrompt, setShowProactivePrompt] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const proactiveTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let storedSessionId = localStorage.getItem('loopbaan_chat_session');
    if (!storedSessionId) {
      storedSessionId = uuidv4();
      localStorage.setItem('loopbaan_chat_session', storedSessionId);
    }
    setSessionId(storedSessionId);

    // Check authentication status
    supabase.auth.getUser().then(({ data: { user } }) => {
      setIsAuthenticated(!!user);
    });
  }, []);

  // Proactive chat timer - 2 min for anonymous, 5 min for authenticated
  useEffect(() => {
    // Don't show if chat was already opened or prompt was dismissed
    const hasInteracted = localStorage.getItem('loopbaan_chat_interacted');
    if (hasInteracted === 'true' || isOpen) {
      return;
    }

    const interval = isAuthenticated ? 5 * 60 * 1000 : 2 * 60 * 1000; // 5 min or 2 min
    
    proactiveTimerRef.current = setTimeout(() => {
      setShowProactivePrompt(true);
    }, interval);

    return () => {
      if (proactiveTimerRef.current) {
        clearTimeout(proactiveTimerRef.current);
      }
    };
  }, [isAuthenticated, isOpen]);

  // Track chat interaction
  useEffect(() => {
    if (isOpen) {
      localStorage.setItem('loopbaan_chat_interacted', 'true');
      setShowProactivePrompt(false);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getUserContext = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return {
        currentPage: window.location.pathname,
        utm: {
          utm_source: new URLSearchParams(window.location.search).get('utm_source'),
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign')
        }
      };
    }

    const { data: credits } = await supabase
      .from('credits')
      .select('total_credits, plan_type')
      .eq('user_id', user.id)
      .single();

    const { count: totalApplications } = await supabase
      .from('extension_tasks')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .in('status', ['applied', 'completed'] as any);

    return {
      currentPage: window.location.pathname,
      creditsRemaining: credits?.total_credits || 0,
      subscriptionStatus: credits?.plan_type || 'free',
      totalApplications: totalApplications || 0
    };
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user' as const, content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const userContext = await getUserContext();
      const { data: { session } } = await supabase.auth.getSession();

      const response = await supabase.functions.invoke('chat-completion', {
        body: { messages: [...messages, userMessage], sessionId, userContext },
        headers: session?.access_token 
          ? { Authorization: `Bearer ${session.access_token}` }
          : {}
      });

      if (response.error) throw response.error;

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.message
      }]);

    } catch (error: any) {
      toast.error(error.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 rounded-full w-14 h-14 shadow-lg z-50"
          size="icon"
        >
          <MessageSquare className="w-6 h-6" />
        </Button>

        {/* Proactive prompt */}
        {showProactivePrompt && (
          <div className="fixed bottom-24 right-6 bg-background border shadow-xl rounded-lg p-4 max-w-sm z-50 animate-in slide-in-from-bottom-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm mb-1">
                  {isAuthenticated 
                    ? "Need help with your job search?" 
                    : "Have questions about LoopBaan?"}
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  {isAuthenticated
                    ? "I can help with credits, applications, or features!"
                    : "Ask me about pricing, features, or how to get started!"}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => setIsOpen(true)}
                    className="flex-1"
                  >
                    Chat now
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowProactivePrompt(false)}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 w-96 bg-background rounded-lg shadow-2xl border flex flex-col z-50 transition-all ${
        isMinimized ? 'h-14' : 'h-[600px]'
      }`}
    >
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">LoopBaan AI</h3>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" onClick={() => setIsMinimized(!isMinimized)}>
            <Minimize2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center text-muted-foreground mt-8">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Hi! How can I help you today?</p>
                <p className="text-xs mt-1">Ask about features, pricing, or getting started!</p>
              </div>
            )}
            
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg p-3 ${
                  msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg p-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce [animation-delay:0.1s]" />
                    <div className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce [animation-delay:0.2s]" />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Type your message..."
                disabled={isLoading}
              />
              <Button onClick={sendMessage} disabled={isLoading || !input.trim()} size="icon">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
